// API calls for the hidden Advanced Settings app. All requests go through the shared
// `API` client (JWT auto-attached), same as the rest of the app — nothing here is a new
// auth path. Endpoint keys: 'api' = platform-api, 'manager' = c2-manager.
import { API } from '../Auth';
import {
  listSamplingRuleTemplates,
  updateSamplingRuleTemplate,
} from '../DataDiscovery/samplingRulesApi';
import { listMaskingAlgorithms } from '../Secure/api';

const PLATFORM = 'api';
const MANAGER = 'manager';

// --- Advanced config singleton (entity levels + entity->algorithm overrides) --------------

// Read is open to any authenticated user (see AdvancedConfigController); the main app also
// calls this on boot to honor entity levels for every user.
export async function getAdvancedConfig() {
  const res = await API.get(PLATFORM, '/api/v1/advanced-config');
  return normalizeConfig(res);
}

// Write is admin-only (PUT /api/admin/advanced-config). The backend does a full replace of all
// fields. To keep callers from wiping fields they don't know about (the historic footgun:
// portExposureEnabled was cleared by every save from this app), this helper loads the current
// config first and merges ONLY the keys the caller explicitly provided over it — an explicit
// null still clears that field (tri-states: null → worker/manager default), absent keys keep
// their stored value. maxRowsPerTable: number to set, or null to clear (→ deployment default).
// profileEnabled: boolean toggle for the Schema Metadata feature. scanEmbeddedStructures /
// columnStatsEnabled / refillEnabled / verifySweepEnabled / portExposureEnabled: TRI-STATE —
// true/false to force, null to clear. NOT coerced with `!!`: that would turn "unset" into
// "off" install-wide.
export async function saveAdvancedConfig(changes = {}) {
  const current = await getAdvancedConfig();
  const merged = { ...current };
  Object.keys(changes).forEach((k) => {
    if (Object.prototype.hasOwnProperty.call(changes, k)) merged[k] = changes[k];
  });
  const res = await API.put(PLATFORM, '/api/admin/advanced-config', {
    body: {
      entityLevels: merged.entityLevels || {},
      entityTypeToAlgorithm: merged.entityTypeToAlgorithm || {},
      // Healthcare (Safe Harbor) profile overrides — stored separately server-side; merged
      // over the shipped safe-harbor overlay for healthcare-tagged sources only.
      entityTypeToAlgorithmSafeHarbor: merged.entityTypeToAlgorithmSafeHarbor || {},
      maxRowsPerTable: merged.maxRowsPerTable != null ? merged.maxRowsPerTable : null,
      profileEnabled: !!merged.profileEnabled,
      scanEmbeddedStructures:
        merged.scanEmbeddedStructures != null ? !!merged.scanEmbeddedStructures : null,
      // Ordered entity-code list for "most important PII first" displays. Empty/absent → the
      // UI falls back to its shipped default order (Utils/entityPriority.js).
      entityOrder: Array.isArray(merged.entityOrder) ? merged.entityOrder : [],
      portExposureEnabled:
        merged.portExposureEnabled != null ? !!merged.portExposureEnabled : null,
      // Discovery-coverage knobs (profile-before-discover gate, per-column profile stats,
      // sparse-column refill, post-discovery verify sweep).
      profileFreshnessHours:
        merged.profileFreshnessHours != null ? merged.profileFreshnessHours : null,
      columnStatsEnabled:
        merged.columnStatsEnabled != null ? !!merged.columnStatsEnabled : null,
      refillEnabled: merged.refillEnabled != null ? !!merged.refillEnabled : null,
      refillSparseThreshold:
        merged.refillSparseThreshold != null ? merged.refillSparseThreshold : null,
      verifySweepEnabled:
        merged.verifySweepEnabled != null ? !!merged.verifySweepEnabled : null,
      // "Discover everything" fan-out caps — null = use the deployment default (25 / 3).
      autofanoutMaxChildrenPerRun:
        merged.autofanoutMaxChildrenPerRun != null ? merged.autofanoutMaxChildrenPerRun : null,
      autofanoutMaxConcurrentChildren:
        merged.autofanoutMaxConcurrentChildren != null ? merged.autofanoutMaxConcurrentChildren : null,
      // Install-wide gate for the data-source Import/Export controls (C2D-1000/998). Plain
      // boolean — "off" and "unset" are the same for a pure UI feature gate.
      dataSourceImportExportEnabled: !!merged.dataSourceImportExportEnabled,
      // Enterprise Data Landscape severity scale. Always sent (read-modify-write) so a save from
      // any screen preserves it; the server sanitises and merges it over the shipped defaults.
      landscapeSeverity: merged.landscapeSeverity && typeof merged.landscapeSeverity === 'object'
        ? merged.landscapeSeverity
        : null,
      // SMTP override block. null = "use the deployment env default" per field. smtpEnabled is
      // TRI-STATE like the other feature knobs. smtpPasswordSet and smtpEnvDefaults are
      // read-only and never sent. The write-only smtpPassword (set a new stored password) and
      // smtpPasswordClear (drop the stored override) ride along ONLY when the caller explicitly
      // provided them — an absent smtpPassword means "keep the stored password".
      smtpEnabled: merged.smtpEnabled != null ? !!merged.smtpEnabled : null,
      smtpHost: merged.smtpHost != null ? merged.smtpHost : null,
      smtpPort: merged.smtpPort != null ? merged.smtpPort : null,
      smtpTlsMode: merged.smtpTlsMode != null ? merged.smtpTlsMode : null,
      smtpUsername: merged.smtpUsername != null ? merged.smtpUsername : null,
      smtpFromAddress: merged.smtpFromAddress != null ? merged.smtpFromAddress : null,
      ...(typeof merged.smtpPassword === 'string' && merged.smtpPassword !== ''
        ? { smtpPassword: merged.smtpPassword }
        : {}),
      ...(merged.smtpPasswordClear === true ? { smtpPasswordClear: true } : {}),
      // User email-domain policy. ALWAYS sent (full-replace PUT): empty array = no
      // restriction. The server normalizes entries (trim, lowercase, strips leading '@',
      // drops dotless entries, dedupes) — the UI only splits/trims.
      emailDomainWhitelist: Array.isArray(merged.emailDomainWhitelist)
        ? merged.emailDomainWhitelist
        : [],
      emailDomainBlacklist: Array.isArray(merged.emailDomainBlacklist)
        ? merged.emailDomainBlacklist
        : [],
    },
  });
  return normalizeConfig(res);
}

const numberOrNull = (v) => (v != null && Number.isFinite(Number(v)) ? Number(v) : null);
const triState = (v) => (v != null ? !!v : null);

function normalizeConfig(res) {
  const cfg = res && typeof res === 'object' ? res : {};
  return {
    entityLevels: cfg.entityLevels && typeof cfg.entityLevels === 'object' ? cfg.entityLevels : {},
    entityTypeToAlgorithm:
      cfg.entityTypeToAlgorithm && typeof cfg.entityTypeToAlgorithm === 'object'
        ? cfg.entityTypeToAlgorithm
        : {},
    entityTypeToAlgorithmSafeHarbor:
      cfg.entityTypeToAlgorithmSafeHarbor && typeof cfg.entityTypeToAlgorithmSafeHarbor === 'object'
        ? cfg.entityTypeToAlgorithmSafeHarbor
        : {},
    // Read-only shipped safe-harbor overlay (never sent on save) — lets the Algorithm Mapping
    // screen render the healthcare profile's defaults.
    safeHarborDefaults:
      cfg.safeHarborDefaults && typeof cfg.safeHarborDefaults === 'object'
        ? cfg.safeHarborDefaults
        : {},
    maxRowsPerTable: numberOrNull(cfg.maxRowsPerTable),
    profileEnabled: !!cfg.profileEnabled,
    // Preserved as null when unset — null is a real state ("use the worker default"), so it must
    // survive the round-trip that every screen does. Coercing to a boolean here would make the
    // next save from ANY screen pin the flag off.
    scanEmbeddedStructures: triState(cfg.scanEmbeddedStructures),
    // Ordered entity-code list (may be empty → consumers use the shipped default order).
    entityOrder: Array.isArray(cfg.entityOrder)
      ? cfg.entityOrder.filter((c) => typeof c === 'string' && c.trim())
      : [],
    portExposureEnabled: triState(cfg.portExposureEnabled),
    profileFreshnessHours: numberOrNull(cfg.profileFreshnessHours),
    columnStatsEnabled: triState(cfg.columnStatsEnabled),
    refillEnabled: triState(cfg.refillEnabled),
    refillSparseThreshold: numberOrNull(cfg.refillSparseThreshold),
    verifySweepEnabled: triState(cfg.verifySweepEnabled),
    autofanoutMaxChildrenPerRun: numberOrNull(cfg.autofanoutMaxChildrenPerRun),
    autofanoutMaxConcurrentChildren: numberOrNull(cfg.autofanoutMaxConcurrentChildren),
    dataSourceImportExportEnabled: !!cfg.dataSourceImportExportEnabled,
    // Effective EDL severity scale {tiers:[{level,minFindings,entities}]} and the read-only
    // shipped defaults (never sent back) for the Reset button.
    landscapeSeverity: normalizeSeverity(cfg.landscapeSeverity),
    landscapeSeverityDefaults: normalizeSeverity(cfg.landscapeSeverityDefaults),
    // SMTP overrides — all nullable ("use deployment env default"), preserved as null so the
    // read-modify-write in saveAdvancedConfig never turns "unset" into a forced value.
    smtpEnabled: triState(cfg.smtpEnabled),
    smtpHost: cfg.smtpHost != null ? String(cfg.smtpHost) : null,
    smtpPort: numberOrNull(cfg.smtpPort),
    smtpTlsMode: cfg.smtpTlsMode != null ? String(cfg.smtpTlsMode) : null,
    smtpUsername: cfg.smtpUsername != null ? String(cfg.smtpUsername) : null,
    smtpFromAddress: cfg.smtpFromAddress != null ? String(cfg.smtpFromAddress) : null,
    // Read-only: whether a password override is stored, and the deployment env defaults
    // ({enabled, host, port, tlsMode, username, from, passwordSet}) for "Default (…)" hints.
    smtpPasswordSet: !!cfg.smtpPasswordSet,
    smtpEnvDefaults:
      cfg.smtpEnvDefaults && typeof cfg.smtpEnvDefaults === 'object' ? cfg.smtpEnvDefaults : {},
    // User email-domain policy (server-normalized). Empty = no restriction; the blacklist
    // wins over the whitelist server-side. Round-tripped on every save (full-replace PUT).
    emailDomainWhitelist: Array.isArray(cfg.emailDomainWhitelist)
      ? cfg.emailDomainWhitelist.filter((d) => typeof d === 'string' && d.trim())
      : [],
    emailDomainBlacklist: Array.isArray(cfg.emailDomainBlacklist)
      ? cfg.emailDomainBlacklist.filter((d) => typeof d === 'string' && d.trim())
      : [],
  };
}

export const SEVERITY_LEVELS = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];

function normalizeSeverity(block) {
  const tiers = block && Array.isArray(block.tiers) ? block.tiers : [];
  const byLevel = {};
  tiers.forEach((t) => {
    const level = String(t?.level || '').toUpperCase();
    if (!SEVERITY_LEVELS.includes(level) || byLevel[level]) return;
    byLevel[level] = {
      level,
      minFindings: Number.isFinite(Number(t.minFindings)) && Number(t.minFindings) >= 1 ? Number(t.minFindings) : 1,
      entities: Array.isArray(t.entities) ? t.entities.filter((c) => typeof c === 'string' && c.trim()) : [],
    };
  });
  return { tiers: SEVERITY_LEVELS.map((level) => byLevel[level] || { level, minFindings: 1, entities: [] }) };
}

// --- Algorithm mapping screen -------------------------------------------------------------

// The shipped/default C2 baseline map merged server-side with any saved override — this is
// what the Algorithm Mapping screen shows as the current effective map. Passing no serviceId
// returns the C2 baseline (see WorkerCatalogController.c2BaselineMap).
export async function getEntityAlgorithmMap() {
  const res = await API.get(PLATFORM, '/api/v1/masking/entity-algorithm-map');
  return res && typeof res === 'object' ? res : {};
}

export async function getMaskingAlgorithms() {
  const list = await listMaskingAlgorithms();
  return Array.isArray(list) ? list : [];
}

// --- Settings screen (reuses existing endpoints) ------------------------------------------

export async function getDiscoverySettings() {
  return API.get(MANAGER, '/api/admin/discovery-settings');
}

export async function saveDiscoverySettings(enabled, intervalMinutes) {
  return API.put(MANAGER, '/api/admin/discovery-settings', {
    body: { enabled, intervalMinutes },
  });
}

// --- Known database ports (c2-manager landscape port classification) ----------------------
// Response: { portWindow, engines: [...], ports: [{ id, engine, port, canonical }] } ordered
// canonical-first. Canonical rows are the built-in defaults and cannot be deleted; custom
// rows are classified as exact matches by the landscape port scanner.

export async function getDbPortDefaults() {
  return API.get(MANAGER, '/api/admin/db-port-defaults');
}

export async function addDbPortDefault(engine, port) {
  return API.post(MANAGER, '/api/admin/db-port-defaults', { body: { engine, port } });
}

export async function deleteDbPortDefault(id) {
  return API.del(MANAGER, `/api/admin/db-port-defaults/${encodeURIComponent(id)}`);
}

export { listSamplingRuleTemplates, updateSamplingRuleTemplate };

// Read-only platform config knobs (currently YAML/env only) surfaced for visibility. Two
// sources: platform-api and c2-manager. Each returns [{ label, value, description }].
export async function getPlatformConfig() {
  const [api, manager] = await Promise.all([
    API.get(PLATFORM, '/api/admin/platform-config').catch(() => []),
    API.get(MANAGER, '/api/admin/platform-config').catch(() => []),
  ]);
  return {
    platform: Array.isArray(api) ? api : [],
    manager: Array.isArray(manager) ? manager : [],
  };
}

// --- Algorithm aliases (masking worker alias catalog) -------------------------------------
// A "base" (a LOOKUP replacement-pool base type, a STANDARD algorithm, or a CUSTOM_JAR base
// registered per uploaded custom algorithm) ships fixed built-in behavior. Saving an alias here
// creates a separately-named algorithm that shows up in every algorithm dropdown elsewhere in
// Advanced Config (Entity -> Algorithm Mapping) and the masking inventory. Write endpoints are
// admin-only; reads are open (mirrors the advanced-config singleton split above). Shapes come
// from the masking worker verbatim — see AliasesScreen.js for field-level notes.

export async function listAliasBases() {
  const res = await API.get(PLATFORM, '/api/v1/masking/aliases/bases');
  return Array.isArray(res) ? res : [];
}

// base: optional base name to filter server-side; omitted, returns every alias across every
// base (the screen groups them client-side).
export async function listAliases(base) {
  const qs = base ? `?base=${encodeURIComponent(base)}` : '';
  const res = await API.get(PLATFORM, `/api/v1/masking/aliases${qs}`);
  return Array.isArray(res) ? res : [];
}

// Detail includes `values` for USER lookup aliases (never present on the list-endpoint
// summaries) — the only reason callers need this beyond listAliases().
export async function getAlias(name) {
  return API.get(PLATFORM, `/api/v1/masking/aliases/${encodeURIComponent(name)}`);
}

// payload: { base, params?, values?, description? }. Errors: 400 validation, 403 shipped
// (editing a SHIPPED alias), 404 unknown base, 409 name collision with { message }.
export async function saveAlias(name, payload) {
  return API.put(PLATFORM, `/api/admin/masking/aliases/${encodeURIComponent(name)}`, { body: payload });
}

// force: when true, appends ?force=true so the backend proceeds even though the alias is
// still referenced elsewhere (backend otherwise 409s with { referencedBy: [...] } so the
// caller can confirm with the user first). 403 if the alias is SHIPPED.
export async function deleteAlias(name, force) {
  const qs = force ? '?force=true' : '';
  return API.del(PLATFORM, `/api/admin/masking/aliases/${encodeURIComponent(name)}${qs}`);
}

// --- Custom masking algorithms (custom jar upload) ----------------------------------------
// A custom algorithm is a user-supplied jar implementing MaskingAlgorithm, uploaded to the
// masking worker via this platform-api proxy. Names must match ^[A-Za-z][A-Za-z0-9_]{2,63}$
// (same rule as custom lookup lists). An algorithm is only added to the regular
// catalog/metadata endpoints (getMaskingAlgorithms) after it passes a test run here. Read is
// open to any authenticated user; upload/test/delete are admin-only (mirrors the lookup-list
// split above).

export async function listCustomAlgorithms() {
  const res = await API.get(PLATFORM, '/api/v1/masking/custom-algorithms');
  return Array.isArray(res) ? res : [];
}

// `file`: a File object from an <input type="file">. `replace`: true to overwrite an existing
// algorithm of the same name (backend 409s otherwise). Worker-rejected uploads come back as
// the platform's structured error envelope — on 422 (jar accepted, class failed to load) the
// body carries {error, kind, stackSummary} as top-level keys (see e.body.* below).
export async function uploadCustomAlgorithm({ file, name, className, replace }) {
  const form = new FormData();
  form.append('file', file);
  form.append('name', name);
  form.append('className', className);
  const qs = replace ? '?replace=true' : '';
  return API.post(PLATFORM, `/api/admin/masking/custom-algorithms${qs}`, form);
}

// Edit an existing algorithm; any subset of { newName, className, file } (at least one). A
// rename keeps the algorithm active and cascades server-side (jar, registry, aliases, and any
// entity->algorithm mappings pointing at the old name). A className or jar change is validated
// by the worker first (422 with {error, kind, stackSummary} on failure, nothing changed) and on
// success resets the algorithm to "Pending test" — it must pass a test again to reactivate.
// Errors: 404 unknown name, 409 newName collision, 400 nothing to update.
export async function updateCustomAlgorithm(name, { newName, className, file } = {}) {
  const form = new FormData();
  if (newName) form.append('newName', newName);
  if (className) form.append('className', className);
  if (file) form.append('file', file);
  return API.post(PLATFORM, `/api/admin/masking/custom-algorithms/${encodeURIComponent(name)}`, form);
}

// { sampleText, config } — config is an optional plain object (advanced JSON), passed through
// to the worker verbatim. Response: { success, input, output?, warning?, durationMs?, error?,
// stackSummary?, registered }.
export async function testCustomAlgorithm(name, { sampleText, config } = {}) {
  const body = { sampleText, ...(config !== undefined && config !== null && { config }) };
  return API.post(PLATFORM, `/api/admin/masking/custom-algorithms/${encodeURIComponent(name)}/test`, { body });
}

// force: when true, appends ?force=true so the backend proceeds even though the algorithm is
// still referenced by an entity->algorithm mapping (backend otherwise 409s with
// { referencedBy: [...] } so the caller can confirm with the user first).
export async function deleteCustomAlgorithm(name, force) {
  const qs = force ? '?force=true' : '';
  return API.del(PLATFORM, `/api/admin/masking/custom-algorithms/${encodeURIComponent(name)}${qs}`);
}

// --- Custom entity detectors (custom jar upload, discover worker) ------------------------
// Mirrors the custom-algorithm block above for the discover worker's /api/v1/config/detectors.
// A detector is a jar implementing com.c2data.detector.spi.CustomEntityDetector. Same name rule
// (^[A-Za-z][A-Za-z0-9_]{2,63}$). Two differences from algorithms: the worker smoke-tests the
// jar AT UPLOAD (201 = already active, its entity types are in the catalog), and there is no
// edit endpoint — replacing is a re-upload with replace=true. Read is open to any authenticated
// user; upload/test/delete are admin-only.

export async function listCustomDetectors() {
  const res = await API.get(PLATFORM, '/api/v1/discovery/custom-detectors');
  return Array.isArray(res) ? res : [];
}

// `file`: a File object from an <input type="file">. `description` optional. `replace`: true to
// overwrite an existing detector of the same name (backend 409s otherwise). On 422 (jar
// rejected — class not found / not a detector / smoke test failed) the body carries
// {error, kind, stackSummary} as top-level keys; stackSummary is an ARRAY of lines here.
export async function uploadCustomDetector({ file, name, className, description, replace }) {
  const form = new FormData();
  form.append('file', file);
  form.append('name', name);
  form.append('className', className);
  if (description) form.append('description', description);
  const qs = replace ? '?replace=true' : '';
  return API.post(PLATFORM, `/api/admin/discovery/custom-detectors${qs}`, form);
}

// No input — the worker owns the sample batches. Response: { success, entityTypes,
// detectionsByShape: { columnar|json|xml|document: [..] }, error?, stackSummary? }.
export async function testCustomDetector(name) {
  return API.post(PLATFORM, `/api/admin/discovery/custom-detectors/${encodeURIComponent(name)}/test`, { body: {} });
}

export async function deleteCustomDetector(name) {
  return API.del(PLATFORM, `/api/admin/discovery/custom-detectors/${encodeURIComponent(name)}`);
}

// --- Email (SMTP) test --------------------------------------------------------------------

// Sends a test email through the currently-effective SMTP settings (stored overrides merged
// over the deployment env defaults). 200 -> { sent: true, warning?: "..." }; a 422
// ({ sent: false, error: "..." }) surfaces as ApiError with the body attached, so callers
// show e.body.error verbatim.
export async function sendTestEmail(to) {
  return API.post(PLATFORM, '/api/admin/email/test', { body: { to } });
}

// --- Health & Errors screen ---------------------------------------------------------------

export async function getClusterHealth() {
  const res = await API.get(PLATFORM, '/api/admin/health');
  return Array.isArray(res) ? res : [];
}

export async function getErrorHistory({ page = 0, size = 50 } = {}) {
  const res = await API.get(PLATFORM, `/api/admin/errors?page=${page}&size=${size}`);
  // Spring Page shape: { content: [...], totalElements, totalPages, number, ... }
  if (res && Array.isArray(res.content)) return res;
  return { content: Array.isArray(res) ? res : [], totalElements: 0, totalPages: 0, number: 0 };
}
